"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  StoreCart,
  StoreCartShippingOption,
  StoreCustomer,
} from "@medusajs/types"
import { listCartOptions, retrieveCart } from "@lib/data/cart"
import { retrieveCustomer } from "@lib/data/customer"

const CART_COUNT_STORAGE_KEY = "alif:cart:count"
const CART_UPDATED_EVENT = "alif:cart-updated"

type CartContextValue = {
  cart: StoreCart | null
  customer: StoreCustomer | null
  shippingOptions: StoreCartShippingOption[]
  totalItems: number
  isHydrated: boolean
  refresh: () => Promise<void>
}

const DEFAULT_VALUE: CartContextValue = {
  cart: null,
  customer: null,
  shippingOptions: [],
  totalItems: 0,
  isHydrated: false,
  refresh: async () => {},
}

const CartContext = createContext<CartContextValue | null>(null)

function readCachedCount(): number {
  if (typeof window === "undefined") return 0
  try {
    const raw = window.localStorage.getItem(CART_COUNT_STORAGE_KEY)
    const n = raw ? parseInt(raw, 10) : 0
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch {
    return 0
  }
}

function writeCachedCount(count: number) {
  if (typeof window === "undefined") return
  try {
    if (count > 0) {
      window.localStorage.setItem(CART_COUNT_STORAGE_KEY, String(count))
    } else {
      window.localStorage.removeItem(CART_COUNT_STORAGE_KEY)
    }
  } catch {
    /* swallow quota / disabled storage */
  }
}

function countCartItems(cart: StoreCart | null): number {
  return cart?.items?.reduce((acc, item) => acc + (item.quantity || 0), 0) || 0
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<StoreCart | null>(null)
  const [customer, setCustomer] = useState<StoreCustomer | null>(null)
  const [shippingOptions, setShippingOptions] = useState<
    StoreCartShippingOption[]
  >([])
  const [isHydrated, setIsHydrated] = useState(false)
  const [cachedCount, setCachedCount] = useState(0)

  const refresh = useCallback(async () => {
    try {
      const [c, cust] = await Promise.all([
        retrieveCart().catch(() => null),
        retrieveCustomer().catch(() => null),
      ])

      setCart(c)
      setCustomer(cust)
      writeCachedCount(countCartItems(c))

      if (c) {
        try {
          const { shipping_options } = await listCartOptions()
          setShippingOptions(shipping_options || [])
        } catch {
          setShippingOptions([])
        }
      } else {
        setShippingOptions([])
      }
    } finally {
      setIsHydrated(true)
    }
  }, [])

  useEffect(() => {
    // first paint: hydrate cached count from previous visit (no flash on returning users)
    setCachedCount(readCachedCount())
    refresh()

    const onUpdate = () => {
      refresh()
    }
    window.addEventListener(CART_UPDATED_EVENT, onUpdate)
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, onUpdate)
    }
  }, [refresh])

  const totalItems = isHydrated ? countCartItems(cart) : cachedCount

  const value: CartContextValue = {
    cart,
    customer,
    shippingOptions,
    totalItems,
    isHydrated,
    refresh,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  return ctx ?? DEFAULT_VALUE
}

export function notifyCartChanged() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(CART_UPDATED_EVENT))
}
